import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, BookOpen, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/risk-disclosure")({
  head: () => ({
    meta: [
      { title: "Risk Disclosure — ForexMind AI" },
      {
        name: "description",
        content:
          "Full risk disclosure for ForexMind AI. Educational market intelligence only — not financial advice. Forex and CFD trading involves significant risk of loss.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Risk Disclosure — ForexMind AI" },
      {
        property: "og:description",
        content:
          "Read the full risk disclosure covering forex and CFD risk, leverage, AI limitations, and jurisdiction restrictions.",
      },
      { property: "og:url", content: "https://forexmind.ai/risk-disclosure" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Risk Disclosure — ForexMind AI" },
      {
        name: "twitter:description",
        content:
          "Read the full risk disclosure covering forex and CFD risk, leverage, AI limitations, and jurisdiction restrictions.",
      },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/risk-disclosure" }],
  }),
  component: RiskDisclosurePage,
});

const sections: Array<{ title: string; body: React.ReactNode }> = [
  {
    title: "Educational purpose only",
    body: (
      <>
        ForexMind AI is an AI-powered market intelligence and educational decision-support
        platform. All content, including agent commentary, setup detection, conviction scoring,
        and historical playbook matches, is provided strictly for educational and informational
        purposes.
      </>
    ),
  },
  {
    title: "No financial advice",
    body: (
      <>
        Nothing on the platform constitutes financial advice, investment advice, tax advice,
        legal advice, or a personal recommendation to buy, sell, or hold any financial
        instrument.
      </>
    ),
  },
  {
    title: "No investment advisory relationship",
    body: (
      <>
        Use of ForexMind AI does not create an advisor–client relationship. We do not assess
        your personal financial situation, objectives, or risk tolerance, and we do not
        provide individualized recommendations.
      </>
    ),
  },
  {
    title: "No brokerage or trade execution",
    body: (
      <>
        ForexMind AI does not execute trades, hold client funds, manage accounts, or operate
        as a broker, dealer, money manager, or commodity trading advisor.
      </>
    ),
  },
  {
    title: "Forex and CFD risk warning",
    body: (
      <>
        Foreign exchange (forex) and CFD trading involves a high level of risk and may not be
        suitable for all investors. You could sustain a loss of some or all of your initial
        capital. Only trade with capital you can afford to lose.
      </>
    ),
  },
  {
    title: "Leverage risk",
    body: (
      <>
        Leverage amplifies both gains and losses. Small adverse market moves can produce
        disproportionately large losses, including amounts exceeding your initial deposit at
        some brokers.
      </>
    ),
  },
  {
    title: "Past performance disclaimer",
    body: (
      <>
        Past performance of any market intelligence shown on this platform is not indicative
        of future results. Aggregate historical accuracy figures are published for transparency
        and should not be interpreted as a forecast.
      </>
    ),
  },
  {
    title: "AI limitations",
    body: (
      <>
        AI outputs are model-generated and may be incomplete, delayed, biased, or incorrect.
        Confluence scores, conviction levels, and agent reasoning are heuristics, not
        guarantees. They should not be relied upon as the sole basis for any trading decision.
      </>
    ),
  },
  {
    title: "Data delay / data error risk",
    body: (
      <>
        Market data is sourced from third-party providers and may be delayed, throttled,
        rate-limited, or temporarily incorrect due to upstream issues. Do not rely on the
        platform for time-critical decisions.
      </>
    ),
  },
  {
    title: "User responsibility",
    body: (
      <>
        You are solely responsible for any trading decisions you make, for evaluating the
        suitability of any setup for your personal circumstances, and for compliance with the
        laws and regulations applicable to you.
      </>
    ),
  },
  {
    title: "Jurisdiction restrictions",
    body: (
      <>
        Access to forex/CFD-related content may be restricted in certain jurisdictions
        (including but not limited to the United States, Canada, Belgium, and New Zealand).
        You confirm you are legally permitted to access the platform from your location.
      </>
    ),
  },
  {
    title: "No guarantee of availability",
    body: (
      <>
        We do not guarantee uninterrupted availability, accuracy, completeness, or error-free
        operation of the service. Features may change or be withdrawn without notice.
      </>
    ),
  },
  {
    title: "Contact / support",
    body: (
      <>
        Questions about this disclosure? Email <a className="underline" href="mailto:legal@forexmind.ai">legal@forexmind.ai</a>.
      </>
    ),
  },
];

function RiskDisclosurePage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-12 text-foreground">
      <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
        <Link to="/">
          <ArrowLeft className="mr-1.5 h-3 w-3" /> Back to home
        </Link>
      </Button>

      <div className="mt-6 rounded-lg border border-warn/40 bg-warn/10 p-4 text-warn">
        <div className="mb-1 flex items-center gap-2 font-semibold">
          <AlertTriangle className="h-4 w-4" /> Educational market intelligence only
        </div>
        <p className="text-[12.5px] leading-snug">
          ForexMind AI is not a broker, investment advisor, or financial advisor. The platform
          provides AI-powered market intelligence for educational purposes only. Forex and CFD
          trading involves significant risk of loss. Past performance does not guarantee future
          results.
        </p>
      </div>

      <h1 className="mt-8 font-display text-3xl font-bold">Risk Disclosure</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Please read this disclosure carefully before using any market intelligence on the
        platform.
      </p>

      <div className="mt-8 space-y-6">
        {sections.map((s) => (
          <section key={s.title}>
            <h2 className="text-base font-semibold text-foreground">{s.title}</h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
          </section>
        ))}
      </div>

      <nav className="mt-10 flex flex-wrap gap-4 border-t border-border/60 pt-6 text-xs text-muted-foreground">
        <Link to="/risk-disclosure" className="hover:text-foreground">Risk Disclosure</Link>
        <Link to="/terms" className="hover:text-foreground">Terms</Link>
        <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
        <Link to="/performance-methodology" className="hover:text-foreground inline-flex items-center gap-1">
          <BookOpen className="h-3 w-3" /> Performance Methodology
        </Link>
        <Link to="/performance" className="hover:text-foreground">Performance</Link>
        <a href="mailto:legal@forexmind.ai" className="hover:text-foreground">Contact</a>
      </nav>
    </main>
  );
}
